import type { CAAlertType, CAButtonType } from '@ds/interfaces'
import { defineComponent, type PropType } from 'vue'

import CAIconComponent from '../shared/CAIconComponent'

/**
	* The `CAAlert` component is used to display an alert message.
	* @component CAAlert
	*
	* Types needed for the alert component
	* @typedef {( 'danger' | 'error' | 'help' | 'info' | 'marketing-danger' | 'marketing-help' | 'marketing-info' | 'marketing-success'  | 'marketing-warning' | 'marketing' | 'success' | 'warning')} AlertType - The type of the alert determines the color and icon used.
	*
	* @prop {string} [additionalClasses=''] - Additional classes to be added to the alert.
	* @prop {boolean} [animated=false] - Whether the alert should be animated.
	* @prop {boolean} [blockLayout=false] - Whether to display content in a block format for better readability.
	* @prop {boolean} [centered=false] - Whether the alert should be centered
	* @prop {string} [dataTest=''] - The data-test attribute for the component.
	* @prop {boolean} [dismissed=false] - Whether the alert can be dismissed
	* @prop {boolean} [dismissible=false] - Whether the alert can be dismissed.
	* @prop {string} [icon=''] - The icon to display in the alert. You can pass a filled or outline icon.
	* @prop {boolean} [rounded=true] - Whether the alert edges should be rounded.
	* @prop {('medium' | 'small')} [size='medium'] - The size of the alert.
	* @prop {string} [title=''] - The title of the alert. Optional.
	* @prop {AlertType} [type='info'] - The type of the alert determines the color and icon used.
	*
	* @emit dismiss - Emitted when the alert is dismissed.
	*
	* @slot actions - The content of the alert that will appear to the far right of the title.
	* @slot default - The content of the alert. This can be anything.
	* @slot title - The title of the card. This is a good alternative for when the title is more than one line or plain text.
*/

export default defineComponent({
	name: 'CAAlert',

	props: {
		/**
		 * Additional classes to be added to the alert.
		 * @default ''
		 * @type {string}
		 */
		additionalClasses: { default: '', type: String },
		/**
		 *
		 * Whether the alert should be animated.
		 * @type {boolean}
		 * @default false
		 */
		animated: { default: false, type: Boolean },
		/**
		 * Whether to display content in a block format for better readability.
		 * @default false
		 * @type {boolean}
		 */
		blockLayout: { default: false, type: Boolean },
		/**
		 * Whether the alert should be centered.
		 * @type {boolean}
		 * @default false
		 */
		centered: { default: false, type: Boolean },
		/**
		 * The data-test attribute for the component.
		 * @type {string}
		 * @default ''
		 */
		dataTest: { default: '', type: String },
		/**
		 * Whether the alert can be dismissed.
		 * @type {boolean}
		 * @default false
		 */
		dismissed: { default: false, type: Boolean },
		/**
		 * Whether the alert can be dismissed.
		 * @type {boolean}
		 * @default false
		 */
		dismissible: { default: false, type: Boolean },
		/**
		 * The icon to display in the alert. You can pass a filled or outline icon.
		 * @type {string}
		 * @default ''
		 */
		icon: { default: '', type: String },
		/**
		 * Whether the alert edges should be rounded.
		 * @type {boolean}
		 * @default true
		 */
		rounded: { default: true, type: Boolean },
		/**
		 * The size of the alert.
		 * @type {('medium' | 'small')}
		 * @default 'medium'
		 */
		size: { default: 'medium', type: String as PropType<'medium' | 'small'> },
		/**
		 * The title of the alert. Optional.
		 * @type {string}
		 * @default ''
		 */
		title: { default: '', type: String },
		/**
		 * The type of the alert determines the color and icon used.
		 * @type {AlertType}
		 * @default 'info'
		 * @enum {('danger' | 'error' | 'help' | 'info' | 'marketing-danger' | 'marketing-help' | 'marketing-info' | 'marketing-success'  | 'marketing-warning' | 'marketing' | 'success' | 'warning')}
		 */
		type: { default: 'info', type: String as PropType<CAAlertType> },
	},

	emits: { dismiss: (_event?: Event) => true },

	data(): Data {
		return { show: false }
	},

	computed: {
		buttonType(): CAButtonType {
			switch (this.type) {
				case 'success':
					return 'success'

				case 'danger':
				case 'error':
					return 'danger'

				case 'warning':
					return 'warning'

				case 'info':
					return 'secondary'

				default:
					return 'primary'
			}
		},

		enableActions(): boolean {
			return this.$slots.actions?.() !== undefined
		},

		mainIcon(): string {
			if (this.icon) {
				return this.icon
			}

			switch (this.type) {
				case 'success':
					return 'SVGCheckCircle'

				case 'warning':
					return 'SVGExclamationMarkCircle'

				case 'danger':
				case 'error':
					return 'SVGCircleX'

				default:
					return 'SVGInfo'
			}
		},

		isMarketingType(): boolean {
			return this.type === 'marketing'
				|| this.type === 'marketing-danger'
				|| this.type === 'marketing-help'
				|| this.type === 'marketing-info'
				|| this.type === 'marketing-success'
				|| this.type === 'marketing-warning'
		},

		titleClass(): string {
			switch (this.type) {
				case 'success':
					return 'text-green-500'

				case 'warning':
					return 'text-yellow-800'

				case 'danger':
				case 'error':
					return 'text-red-500'

				case 'help':
					return 'text-brand-blue-500'

				case 'info':
					return 'text-gray-600'

				default:
					return ''
			}
		},
	},

	mounted() {
		if (this.dismissible && this.dismissed) {
			this.dismiss()
		} else {
			this.show = true
		}
	},

	methods: {
		dismiss(event?: Event) {
			this.show = false
			this.$emit('dismiss', event)
		},
	},

	render() {
		return (
			<div>
				{this.show &&
					<div
						class={[
							'ca-alert',
							`ca-alert__${this.type}`,
							{
								'ca-alert__block': this.blockLayout,
								'ca-alert__dismissible': this.dismissible,
								'ca-alert__rounded': this.rounded,
								'ca-alert__centered': this.centered,
								'ca-alert__marketing': this.isMarketingType && this.type !== 'marketing',
							},
							this.additionalClasses,
						]}
						data-test={this.dataTest}
					>
						<div
							class={[
								'ca-alert__content',
								{
									'ca-alert__content--marketing': this.isMarketingType,
									'ca-alert__content--non-marketing': !this.isMarketingType,
								},
							]}
							data-test="ca-alert-content"
						>
							<div class="ca-alert__main-content">
								{!this.isMarketingType &&
									<div>
										<div
											class={[
												'ca-alert__container',
												{ 'has-no-title': this.title === '' && !this.$slots.title?.() },
											]}
										>
											<div class={{ 'inline-flex flex-row justify-start': this.title !== '' || this.$slots.title?.() }}>
												{!this.isMarketingType &&
													<div class="ca-alert__icon-wrapper pr-1">
														<CAIconComponent class="ca-alert__icon" icon={this.mainIcon} />
													</div>
												}

												{this.title && <div class="ca-alert__title" innerHTML={this.title} />}

												{this.$slots.title && <span class="ca-alert__title">{this.$slots.title?.()}</span>}
											</div>

											<div class="ca-alert__body">{this.$slots.default?.()}</div>
										</div>
									</div>
								}

								{this.isMarketingType &&
									<div class="ca-alert__marketing-content">
										<p class="ca-alert__marketing-text">
											{this.title !== '' || this.$slots.title?.() &&
												<strong>
													{this.title !== '' && <span innerHTML={this.title} />}
													{this.$slots.title?.()}
												</strong>
											}

											{this.title !== '' || this.$slots.title?.() &&
												<CAIconComponent icon="SVGClose" />
											}

											{this.$slots.default?.()}
										</p>

										<div class="ca-alert__marketing-actions">{this.$slots.actions?.()}</div>
									</div>
								}
							</div>

							<div class="ca-alert__button-action-container">
								{!this.isMarketingType && this.$slots.actions?.()}

								{this.dismissible && (this.enableActions || this.isMarketingType) &&
									<button
										class="ca-alert__dismissible--button"
										type="button"
										onClick={this.dismiss}
									>
										<span class="sr-only">Dismiss</span>
										<CAIconComponent icon="SVGClose" />
									</button>
								}
							</div>
						</div>

						{this.type === 'marketing' &&
							<div>
								<div aria-hidden="true" class="ca-gradient ca-gradient__background-one">
									<div class="ca-gradient__background--polygon ca-gradient__background-one--polygon" />
								</div>

								<div aria-hidden="true" class="ca-gradient ca-gradient__background-two">
									<div class="ca-gradient__background--polygon ca-gradient__background-two--polygon" />
								</div>
							</div>
						}

						{this.dismissible && !this.enableActions && !this.isMarketingType &&
							<button
								class="ca-alert__dismissible--button"
								type="button"
								onClick={this.dismiss}
							>
								<span class="sr-only">Dismiss</span>
								<CAIconComponent icon="SVGClose" />
							</button>
						}
					</div>
				}
			</div>
		)
	},
})

interface Data {
	show: boolean
}
